import { defineStore } from 'pinia';
import { NoonMarketplace } from '@/v2/modules/products/entities/marketplaces/noon';
import { useProductEditorStore } from '@/v2/modules/products/stores/product-editor/index';

export const useNoonStore = defineStore('noon', {
	state: () => ({
		data: {},
		variants: {},
		loadingAttr: false,
	}),
	getters: {
		// Noon attributes are product-level (single value each) — no slicer/varianter.
		attributes: (state) => (linkedMarketplaceId) => {
			if (!linkedMarketplaceId || !state.data[linkedMarketplaceId]?.attributes) {
				return [];
			}
			return state.data[linkedMarketplaceId].attributes.map((el) => ({
				...el,
				rules: el.required == 1 ? ['required'] : [],
				fetchDataFunction: (params) =>
					NoonMarketplace.getAttributeValues(
						params,
						state.data[linkedMarketplaceId].category?.id,
						el?.marketplace_attribute_id,
					),
			}));
		},
		formValues(state) {
			const productEditorStore = useProductEditorStore();
			const formValues = {};

			productEditorStore.noonLinkedMarketplaces.forEach((m) => {
				if (!state.data[m.id]) return;

				// Product-level attribute values, applied to every variant.
				const linked_variant_attribute_values = (state.data[m.id].attributes || []).flatMap(
					(attribute) => {
						// noon requires a language tag on a localizable attribute and reports
						// completeness per language, so each one is stored as its own row per
						// locale — an English-only value would leave the Arabic listing
						// incomplete with nothing on screen to explain it.
						if (attribute?.is_localizable && attribute?.allow_custom) {
							return [
								['en', attribute.value_en],
								['ar', attribute.value_ar],
							]
								.filter(
									([, value]) => value !== null && value !== undefined && value !== '',
								)
								.map(([locale, value]) => ({
									marketplace_attributes_id: attribute.marketplace_attribute_id,
									marketplace_attribute_value_custom: value,
									locale,
								}));
						}

						return [
							{
								marketplace_attributes_id: attribute.marketplace_attribute_id,
								marketplace_attribute_value_id: !attribute?.allow_custom
									? attribute.value?.id
									: undefined,
								marketplace_attribute_value_custom: attribute?.allow_custom
									? attribute.value
									: undefined,
							},
						];
					},
				);

				const linkedVariants = {};

				productEditorStore.data.variants.forEach((variant) => {
					const linkedVariant = state.data[m.id]?.linked_variants?.find(
						(el) => el.id === variant.id,
					);
					if (!linkedVariant) return;

					linkedVariants[variant.id] = {
						sku: linkedVariant.sku,
						barcode: linkedVariant.barcode,
						marketplace_category_id: state.data[m.id].category?.id,
						linked_variant_attribute_values,
						// extra_data is deliberately NOT sent: it holds the noon identifiers
						// (psku_code, sku_child, family_code, sku_parent) that the editor does
						// not own and must never overwrite.
					};
				});

				formValues[m.id] = {
					linked_product: {},
					linked_variant: linkedVariants,
				};
			});

			return formValues;
		},
	},
	actions: {
		async getCategoryAttributes(categoryID, linkedMarketplaceId, populateExistingValues = false) {
			if (!categoryID) return;

			this.loadingAttr = true;
			try {
				// Read the stored attributes, the way the Trendyol tab does. This used to
				// import them from noon first, on every single visit to the tab, so opening
				// it always waited on a live API call before anything could be drawn. A
				// scheduled sweep keeps the shared tables in step now, and the endpoint below
				// imports a category it finds empty - so a category is fetched from noon at
				// most once rather than every time.
				const data = await NoonMarketplace.getCategoryAttributes(
					categoryID,
					linkedMarketplaceId,
				);
				if (!this.data[linkedMarketplaceId]) this.data[linkedMarketplaceId] = {};
				// A localizable attribute holds one value PER LANGUAGE; everything else keeps
				// the single `value` the rest of the editor uses.
				this.data[linkedMarketplaceId].attributes = (data?.data ?? []).map((a) => ({
					...a,
					value: null,
					value_en: null,
					value_ar: null,
				}));

				if (populateExistingValues) {
					this.setAttributesValues(linkedMarketplaceId);
				}
			} finally {
				this.loadingAttr = false;
			}
		},
		setAttributesValues(linkedMarketplaceId) {
			const variant = this.variants[linkedMarketplaceId]?.[0];
			if (!this.data[linkedMarketplaceId] || !variant) return;

			const stored = variant.linked_variant_attribute_values || [];
			this.data[linkedMarketplaceId].attributes.forEach((attribute) => {
				const rows = stored.filter(
					(el) => el.marketplace_attributes_id == attribute.marketplace_attribute_id,
				);

				if (!rows.length) return;

				// Localizable attributes come back as one row per locale.
				if (attribute.is_localizable && attribute.allow_custom == 1) {
					attribute.value_en =
						rows.find((el) => el.locale === 'en')?.marketplace_attribute_value_custom ?? null;
					attribute.value_ar =
						rows.find((el) => el.locale === 'ar')?.marketplace_attribute_value_custom ?? null;

					return;
				}

				attribute.value =
					rows[0][
						attribute.allow_custom == 1
							? 'marketplace_attribute_value_custom'
							: 'marketplace_attribute_value'
					];
			});
		},
		async setData(marketplacesData) {
			const productEditorStore = useProductEditorStore();

			marketplacesData.forEach((noon) => {
				const linkedVariants = noon?.linked_variant;
				const variants = Object.values(linkedVariants || {}).map((el) => el[0]);

				const data = {
					category: variants[0]?.marketplace_category,
					// Images already tied to THIS noon account. Persistence is the direct
					// /products/product_images endpoints (with linked_marketplace_id), not
					// the form payload — this list is only what the tab displays. Always an
					// array: the shared component binds v-model straight to it.
					linked_product_images: noon?.linked_product?.linked_product_images ?? [],
					attributes: [],
					linked_variants: variants.map((variant) => {
						const productVariant = productEditorStore.data.variants.find(
							(el) => el.id === variant.variant_id,
						);
						return {
							id: variant.variant_id,
							sku: variant.sku,
							barcode: variant.barcode || productVariant?.barcode,
						};
					}),
				};

				this.variants[noon.linked_marketplace_id] = variants;
				this.data[noon.linked_marketplace_id] = data;

				// Load attributes for an already-mapped category and repopulate values.
				if (variants[0]?.marketplace_category?.id) {
					this.getCategoryAttributes(
						variants[0].marketplace_category.id,
						noon.linked_marketplace_id,
						true,
					);
				}
			});
		},
		async submit() {},
	},
});
